import { Grid } from '@/common/grid/Grid';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useNavState } from '@/common/navState/NavStateContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Split } from '@/common/split/Split';
import { Title } from '@/common/title/Title';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box, Button, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { format } from 'sql-formatter';

export const Interact = () => {
  // Context
  const { navState, assign } = useNavState();
  const { org } = usePrincipal();
  const rsrc = useRsrc();
  const module = useModuleFromRoute();

  // State
  const [sql, setSql] = useState(navState.sql);
  const [showTables, setShowTables] = useState(true);

  // Refs
  const gridRef = useRef(null);
  const tableGridRef = useRef(null);
  const textFieldRef = useRef(null);

  useEffect(() => {
    assign('sql', sql);
  }, [sql]);

  const fetchSchemaTables = () => {
    if (!org || !module || !tableGridRef.current) return false;
    const schemaName = `${org.toLowerCase()}_${module.toLowerCase()}`;
    const query = `SELECT table_name, column_name, data_type, character_maximum_length FROM information_schema.columns WHERE table_schema = '${schemaName}' ORDER BY table_name, ordinal_position`;
    rsrc.data(module, 'INTERACT_DATA_PLUGIN', { sql: query }).then(res => {
      const rows = res.rowData ?? [];
      const tableMap = rows.reduce((acc, row) => {
        const tableName = row.table_name;
        const columnName = row.column_name;
        const dataType = row.data_type;
        const maxLength = row.character_maximum_length;
        if (!tableName || !columnName) return acc;
        if (!acc[tableName]) acc[tableName] = [];
        acc[tableName].push({ columnName, dataType, maxLength });
        return acc;
      }, {});

      const treeRows = Object.entries(tableMap).flatMap(([tableName, columns]) => {
        const parent = {
          name: tableName,
          nodeType: 'table',
          path: tableName,
        };
        const children = columns.map(({ columnName, dataType, maxLength }) => {
          const typeLabel = dataType
            ? (maxLength ? `${dataType}(${maxLength})` : dataType)
            : null;
          return {
            name: typeLabel ? `${columnName} (${typeLabel})` : columnName,
            nodeType: 'column',
            path: `${tableName}|${columnName}`,
          };
        });
        return [parent, ...children];
      });

      const colDefs = [
        {
          field: 'name',
          headerName: `${schemaName} Tables`,
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: { suppressCount: true },
          cellStyle: (params) =>
            params.data?.nodeType === 'table'
              ? { color: '#1f5fbf', textDecoration: 'underline', cursor: 'pointer' }
              : undefined,
        },
      ];

      tableGridRef.current.setColDefs(colDefs);
      tableGridRef.current.setDataSource(treeRows);
    });
    return true;
  };

  useEffect(() => {
    if (!showTables) return;
    if (fetchSchemaTables()) return;
    const timeout = setTimeout(() => {
      fetchSchemaTables();
    }, 0);
    return () => clearTimeout(timeout);
  }, [org, module, showTables]);

  const appendTableName = (tableName) => {
    setSql(prev => {
      const base = prev ?? '';
      const next = base.trim().length === 0
        ? `SELECT * FROM ${tableName}`
        : `${base}${base.endsWith(' ') ? '' : ' '}${tableName}`;
      assign('sql', next);
      return next;
    });
    if (textFieldRef.current) {
      textFieldRef.current.focus();
    }
  };

  const handleChange = (event) => {
    setSql(event.target.value);
  };

  // Helper function to compute query boundaries
  const getQueryBoundaries = (text, cursorPos) => {
    if (cursorPos > 0 && text[cursorPos - 1] === ';') {
      cursorPos = cursorPos - 1;
    }
    let start = text.lastIndexOf(';', cursorPos - 1) + 1;
    while (start < text.length && /\s/.test(text[start])) {
      start++;
    }
    let end = text.indexOf(';', cursorPos);
    if (end === -1) {
      end = text.length;
    }
    return [start, end];
  };

  const executeSql = (query) => {
    rsrc.data(module, 'INTERACT_DATA_PLUGIN', { sql: query }).then(res => {
      const cols = res.cols;
      const colDefs = cols.map(col => ({ field: col, headerName: col }));

      // If a basic select, add hyperlinks to code
      if (query.startsWith('SELECT * FROM kr_')) {
        const pattern = /^SELECT\s+\*\s+FROM\s+kr_([^\s\n]+)/i;
        const matchResult = query.match(pattern);
        const entity = matchResult[1].toUpperCase();

        const codeColDef = colDefs.find(colDef => colDef.field === 'code');
        if (codeColDef) {
          codeColDef.entity = entity;
          codeColDef.module = module;
          codeColDef.cellRenderer = 'hyperlinkCellRenderer';
        }
      }

      gridRef.current.setColDefs(colDefs);
      gridRef.current.setDataSource(res.rowData);
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      const target = event.target;
      const start = target.selectionStart;
      const end = target.selectionEnd;
      const newSql = sql.substring(0, start) + '\t' + sql.substring(end);
      setSql(newSql);
      setTimeout(() => {
        target.selectionStart = target.selectionEnd = start + 1;
      }, 0);
    } else if (event.shiftKey && event.key === 'Enter') {
      event.preventDefault();
      const target = event.target;
      const cursorPos = target.selectionStart;
      const [queryStart, queryEnd] = getQueryBoundaries(sql, cursorPos);
      const currentQuery = sql.substring(queryStart, queryEnd).trim();

      if (currentQuery) {
        target.selectionStart = queryStart;
        target.selectionEnd = queryEnd;
        executeSql(currentQuery);
      }
    }
  };

  // Handler for the Auto-Format Query button.
  const handleAutoFormatQuery = () => {
    const target = textFieldRef.current;
    if (target) {
      const cursorPos = target.selectionStart;
      const [queryStart, queryEnd] = getQueryBoundaries(sql, cursorPos);
      const currentQuery = sql.substring(queryStart, queryEnd).trim();
      if (currentQuery) {
        // Format the current query using the named export 'format'
        const formattedQuery = format(currentQuery);
        const newSql = sql.substring(0, queryStart) + formattedQuery + sql.substring(queryEnd);
        setSql(newSql);
        assign('sql', newSql);
        // Update the selection so that the formatted query is highlighted.
        setTimeout(() => {
          target.selectionStart = queryStart;
          target.selectionEnd = queryStart + formattedQuery.length;
        }, 0);
      }
    }
  };

  const handleSingleLineFormat = () => {
    const queries = sql.split(';')
      .map(part => part.trim())
      .filter(part => part.length > 0);
    const formatted = queries.map(query => {
      const pretty = format(query);
      return pretty.replace(/\s+/g, ' ').trim();
    });
    const newSql = formatted.join(';\n');
    setSql(newSql);
    assign('sql', newSql);
  };

  const handleDownload = () => {
    gridRef.current.gridApiRef.current.exportDataAsCsv();
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Title icon={fas.faDatabase} title="Interact" />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => setShowTables(prev => !prev)}>
            {showTables ? 'Hide Tables' : 'Show Tables'}
          </Button>
          <Button variant="contained" onClick={handleAutoFormatQuery}>
            Auto-Format Query
          </Button>
          <Button variant="contained" onClick={handleSingleLineFormat}>
            Single-Line Format
          </Button>
          <Button variant="contained" onClick={handleDownload}>
            Download Results
          </Button>
        </Box>
      </Box>
      {/* <TextField
          variant="outlined"
          label="SQL"
          fullWidth
          multiline
          minRows={1}
          maxRows={10}
          sx={{ marginBottom: '10px' }}
          value={sql}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          inputRef={textFieldRef}
          slotProps={{ htmlInput: { style: { fontSize: '14px', lineHeight: '1.3' } } }}
        />
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pt: 1 }}>
        <Grid
          ref={gridRef}
          gridOptions={{
            rowModelType: 'clientSide',
          }}
        />
      </Box> */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {showTables ? (
          <Split direction="horizontal" initialSplit={20}>
            <Grid
              ref={tableGridRef}
              gridOptions={{
                rowModelType: 'clientSide',
                suppressHorizontalScroll: true,
                treeData: true,
                groupDefaultExpanded: 0,
              }}
              events={{
                onCellClicked: (event) => {
                  if (event?.data?.nodeType === 'table' && event.value) {
                    appendTableName(event.value);
                  }
                }
              }}
            />
            <Split
              direction="vertical"
              initialSplit={10}
            >
              <TextField
                variant="outlined"
                label="SQL"
                fullWidth
                multiline
                value={sql}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                inputRef={textFieldRef}
                slotProps={{ htmlInput: { style: { fontSize: '14px', lineHeight: '1.3' } } }}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  mt: 1,

                  // make the InputBase fill its container
                  '& .MuiOutlinedInput-root': {
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                  },
                  // make the textarea inside scroll
                  '& .MuiOutlinedInput-input': {
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto !important',
                  },
                }}
              />
              <Grid
                ref={gridRef}
                gridOptions={{
                  rowModelType: 'clientSide',
                }}
              />
            </Split>
          </Split>
        ) : (
          <Split
            direction="vertical"
            initialSplit={10}
          >
            <TextField
              variant="outlined"
              label="SQL"
              fullWidth
              multiline
              value={sql}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              inputRef={textFieldRef}
              slotProps={{ htmlInput: { style: { fontSize: '14px', lineHeight: '1.3' } } }}
              sx={{
                flex: 1,
                minHeight: 0,
                mt: 1,

                // make the InputBase fill its container
                '& .MuiOutlinedInput-root': {
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                },
                // make the textarea inside scroll
                '& .MuiOutlinedInput-input': {
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto !important',
                },
              }}
            />
            <Grid
              ref={gridRef}
              gridOptions={{
                rowModelType: 'clientSide',
              }}
            />
          </Split>
        )}
      </Box>
    </>
  );
};
